import { useLocation, Link, useNavigate } from "react-router-dom";
import { CAMPAIGN } from "@/constants/testIds";
import { CheckCircle2, Leaf, Info, ArrowRight } from "lucide-react";
import { RootsMark } from "@/components/RootsMark";

const LIME = "#B7E64B";

export default function CampaignConfirmPage() {
  const { state } = useLocation();
  const navigate = useNavigate();

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-[#F5F7F2]" data-testid={CAMPAIGN.confirm}>
      <header className="bg-[#09292D] text-white">
        <div className="mx-auto flex max-w-[1160px] items-center gap-2 px-5 py-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: LIME }}>
            <RootsMark className="h-5 w-5 text-[#09292D]" />
          </div>
          <p className="font-heading text-lg font-bold">LIAS<span style={{ color: LIME }}>ROOTS</span></p>
        </div>
      </header>
      <div className="mx-auto max-w-[680px] px-5 py-12">{children}</div>
    </div>
  );

  // No state (direct nav / refresh)
  if (!state) {
    return (
      <Shell>
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <h1 className="font-heading text-2xl font-bold text-[#09292D]">Nothing to show here</h1>
          <p className="mt-2 text-sm text-slate-600">This confirmation page is shown right after you register.</p>
          <Link to="/liasroots" className="mt-5 inline-flex items-center gap-1 rounded-full px-5 py-2.5 text-sm font-bold text-[#09292D]" style={{ backgroundColor: LIME }}>
            Go to the pilot page <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Shell>
    );
  }

  if (state.closed) {
    return (
      <Shell>
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <h1 className="font-heading text-2xl font-bold text-[#09292D]">Registration is closed</h1>
          <p className="mt-2 text-sm text-slate-600">Thank you for your interest. Please check back for future opportunities.</p>
        </div>
      </Shell>
    );
  }

  // Duplicate / retry — neutral, never reveals existing details
  if (state.duplicate) {
    return (
      <Shell>
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-[#4e8a1f]" />
          <h1 className="mt-3 font-heading text-2xl font-bold text-[#09292D]">Thanks — we've got your details</h1>
          <p className="mt-2 text-sm text-slate-600">{state.message || "If this email hasn't already registered, we'll be in touch. You don't need to submit again."}</p>
          <Link to="/liasroots" className="mt-5 inline-block text-sm font-semibold text-[#09292D] underline">Back to the pilot page</Link>
        </div>
      </Shell>
    );
  }

  // Successful registration
  return (
    <Shell>
      <div className="rounded-2xl border border-[#9ccc2f] bg-white p-8">
        <CheckCircle2 className="h-12 w-12 text-[#4e8a1f]" />
        <h1 className="mt-3 font-heading text-3xl font-bold text-[#09292D]">You're registered</h1>
        <p className="mt-2 text-sm text-slate-600">Thanks for your interest in the ROOTS pilot. Keep your reference for any follow-up.</p>

        <div className="mt-5 rounded-xl bg-[#f3f8e6] p-5 text-center">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#4e8a1f]">Your reference</p>
          <p className="font-heading text-3xl font-bold tracking-wide text-[#09292D]" data-testid={CAMPAIGN.reference}>{state.reference}</p>
        </div>

        {state.waitlist && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border-l-4 border-purple-400 bg-purple-50 p-3 text-sm text-purple-900">
            <Info className="mt-0.5 h-4 w-4 shrink-0" /> The current cohort is full, so you've joined the waitlist. We'll contact you if a place opens up.
          </div>
        )}
        {state.eligibility_caveat && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border-l-4 border-amber-400 bg-amber-50 p-3 text-sm text-amber-900">
            <Info className="mt-0.5 h-4 w-4 shrink-0" /> The pilot prioritises LIAS member companies. We'll confirm membership and eligibility with you before any place is offered.
          </div>
        )}
        {state.sg_caveat && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border-l-4 border-amber-400 bg-amber-50 p-3 text-sm text-amber-900">
            <Info className="mt-0.5 h-4 w-4 shrink-0" /> The pilot is for companies operating in Singapore. We'll discuss this with you.
          </div>
        )}

        <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4 text-xs leading-relaxed text-slate-600">
          <b>Please note:</b> registering is <b>not</b> a confirmed pilot place. Places are limited to five and offered to selected companies, subject to eligibility, suitability and a written agreed scope. A receipt has been generated for your records.
        </div>

        {state.profile_token && (
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="font-heading text-lg font-bold text-[#09292D]">Help us prepare (optional)</h3>
            <p className="mt-1 text-sm text-slate-600">A few optional questions help us make our discovery call useful. It's completely optional and won't affect your place or queue position — you can do it now or later.</p>
            <button onClick={() => navigate(`/liasroots/profile/${state.profile_token}`)} data-testid={CAMPAIGN.profileLink}
                    className="mt-3 inline-flex items-center gap-1 rounded-full px-5 py-2.5 text-sm font-bold text-[#09292D]" style={{ backgroundColor: LIME }}>
              Add a few details <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        <Link to="/liasroots" className="mt-6 inline-block text-sm font-semibold text-[#09292D] underline">Back to the pilot page</Link>
      </div>
    </Shell>
  );
}
